import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@shared/components/button';
import { FieldHintDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalFooterDirective, ModalShellComponent } from '@shared/components/modal';
import { SelectComponent } from '@shared/components/select';
import { ImportClient, InstitutionImportable, LONGUEURS_MAXIMALES } from '../liste-clients.interface';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';

export interface DialogImporterClientData {
  importables: InstitutionImportable[];
}

@Component({
  selector: 'app-dialog-importer-client',
  imports: [
    ModalShellComponent,
    ModalFooterDirective,
    ButtonComponent,
    FormFieldComponent,
    FieldHintDirective,
    InputComponent,
    SelectComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './dialog-importer-client.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dialog-importer-client.component.scss',
})
export class DialogImporterClientComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  readonly dialogRef = inject<DialogRef<ImportClient, DialogImporterClientComponent>>(DialogRef);
  readonly data = inject<DialogImporterClientData>(DIALOG_DATA);

  selection = new FormControl<InstitutionImportable | null>(null);

  // Reprise annuaire : le mot de passe legacy sera lu côté serveur au moment de l'import
  motDePasseDepuisAnnuaire = signal<boolean>(false);

  nouvelleBaseForm = new FormGroup({
    storeName: new FormControl<string>('', { nonNullable: true }),
    serveur: new FormControl<string>('', { nonNullable: true }),
    baseDeDonnees: new FormControl<string>('', { nonNullable: true }),
    utilisateur: new FormControl<string>('', { nonNullable: true }),
    motDePasse: new FormControl<string>('', { nonNullable: true }),
  });

  // Miroirs signal de la fiche choisie et de la base à créer, lus par le gabarit
  protected readonly institutionChoisie = toSignal(this.selection.valueChanges, { initialValue: this.selection.value });

  private readonly valeursNouvelleBase = toSignal(
    this.nouvelleBaseForm.valueChanges.pipe(map(() => this.nouvelleBaseForm.getRawValue())),
    { initialValue: this.nouvelleBaseForm.getRawValue() },
  );

  constructor() {
    // La sélection d'une fiche pré-remplit la création de base depuis ses valeurs legacy
    this.selection.valueChanges.subscribe(institution => {
      if (!institution) {
        return;
      }
      this.nouvelleBaseForm.setValue({
        storeName: institution.baseDeDonnees ?? '',
        serveur: institution.serveur ?? '',
        baseDeDonnees: institution.baseDeDonnees ?? '',
        utilisateur: institution.utilisateur ?? '',
        motDePasse: '',
      });
      this.motDePasseDepuisAnnuaire.set(institution.motDePassePresent);
    });

    // Un mot de passe saisi manuellement est prioritaire sur la reprise annuaire
    this.nouvelleBaseForm.controls.motDePasse.valueChanges.subscribe(motDePasse => {
      if (motDePasse) {
        this.motDePasseDepuisAnnuaire.set(false);
      }
    });
  }

  // `app-select` porte des clés : on expose le code instit et on retrouve la
  // fiche à la sélection. Le `FormControl` reste la source de vérité, pour ne
  // pas toucher aux abonnements `valueChanges` qui pré-remplissent la base
  protected optionsInstitutions = computed<SelectOption[]>(() =>
    this.data.importables.map(institution => ({
      value: institution.codeInstit,
      label: institution.codeInstit + (institution.label ? ' — ' + institution.label : ''),
    })),
  );

  protected institutionsSelectionnees = computed<string[]>(() => {
    const code = this.codeSelectionne();
    return code ? [code] : [];
  });

  // Miroir signal du code sélectionné : le FormControl n'est pas réactif au sens
  // des signaux, et la sélection est aussi modifiée par le pré-remplissage
  private codeSelectionne = signal<string | null>(null);

  protected choisirInstitution(valeurs: readonly string[]): void {
    const code = valeurs[0] ?? null;
    this.codeSelectionne.set(code);
    this.selection.setValue(code ? (this.data.importables.find(i => i.codeInstit === code) ?? null) : null);
  }

  annuler(): void {
    this.dialogRef.close();
  }

  readonly baseACreer = computed(() => {
    const institution = this.institutionChoisie();
    return institution != null && !institution.baseExistanteDansRegistre;
  });

  readonly peutValider = computed(() => {
    const institution = this.institutionChoisie();
    if (!institution) {
      return false;
    }
    if (institution.baseExistanteDansRegistre) {
      return true;
    }
    const base = this.valeursNouvelleBase();
    return !!base.storeName && !!base.serveur && !!base.baseDeDonnees;
  });

  valider() {
    const institution = this.selection.value;
    if (!institution || !this.peutValider()) {
      return;
    }

    this.dialogRef.close({
      codeInstit: institution.codeInstit,
      baseACreer: institution.baseExistanteDansRegistre
        ? undefined
        : {
            storeName: this.nouvelleBaseForm.controls.storeName.value,
            serveur: this.nouvelleBaseForm.controls.serveur.value,
            baseDeDonnees: this.nouvelleBaseForm.controls.baseDeDonnees.value,
            utilisateur: this.nouvelleBaseForm.controls.utilisateur.value || undefined,
            motDePasse: this.nouvelleBaseForm.controls.motDePasse.value || undefined,
            motDePasseDepuisAnnuaire: this.motDePasseDepuisAnnuaire(),
          },
    });
  }
}
